"use client";

import type React from "react";

import { useEffect, useState } from "react";
import { IonPage, IonContent, IonHeader } from "@ionic/react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Chip,
  Pagination,
  Button,
  IconButton,
  Skeleton,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  TextField,
  InputAdornment,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Tab,
  Tabs,
} from "@mui/material";
import { baseUrl, config, requestID } from "../../common/utils";
import axios from "axios";
import { useHistory } from "react-router";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Edit, 
  Delete, 
  People, 
  Business, 
  CheckCircle, 
  CreditCard, 
  Payments,
  RocketLaunch,
  Verified
} from "@mui/icons-material";
import styles from "./BusinessDashboard.module.css";
import { useBusinessContext } from "../../context/business-context";

interface BusinessType {
  id: string;
  business_id: string;
  name: string;
  address: string;
  phone: string;
  email: string;
  business_type_id: string;
  targetedSavings?: string;
  collectionAmount?: string;
  cycleDays?: string;
}

const BusinessDashboard: React.FC = () => {
  const history = useHistory();

  const {
    setSelectedBusinessId,
    setSelectedBusinessName,
    setSelectedBusinessTypeName,
    setSelectedBusiness
  } = useBusinessContext();

  const [businesses, setBusinesses] = useState<BusinessType[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const itemsPerPage = 6;

  // Subscription States
  const [subscribedIds, setSubscribedIds] = useState<Set<string>>(new Set());
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedBusinessForSub, setSelectedBusinessForSub] = useState<BusinessType | null>(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  useEffect(() => {
    const fetchBusinesses = async () => {
      setLoading(true);
      try {
        const response = await axios.post(
          baseUrl() + "businesss/getMybusinessList",
          { requestID: requestID() },
          config
        );
        const businessData = Array.isArray(response.data.data)
          ? response.data.data
          : [];
        setBusinesses(businessData);
        setTimeout(() => setIsFirstLoad(false), 500);
      } catch (error) {
        console.error("Error fetching businesses:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBusinesses();
  }, []);

  const handleManageStaff = (business: BusinessType) => {
    if (!subscribedIds.has(business.business_id)) return;
    
    setSelectedBusinessId(business.business_id);
    setSelectedBusinessName(business.name);
    setSelectedBusinessTypeName(getBusinessType(business.business_type_id));
    setSelectedBusiness(business);
    history.push(`/folder/staffManagement/${business.business_id}`);
  };

  const openSubscriptionDialog = (business: BusinessType) => {
    setSelectedBusinessForSub(business);
    setActiveStep(0);
    setSubscriptionDialogOpen(true);
  };

  const handleNextStep = () => {
    if (activeStep === 1) {
      setPaymentProcessing(true);
      setTimeout(() => {
        setPaymentProcessing(false);
        setActiveStep(2);
      }, 2000);
    } else if (activeStep === 2) {
      if (selectedBusinessForSub) {
        setSubscribedIds(prev => new Set(prev).add(selectedBusinessForSub.business_id));
      }
      setSubscriptionDialogOpen(false);
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBackStep = () => {
    setActiveStep((prev) => prev - 1);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getBusinessType = (typeId: string) => {
    const types: Record<string, string> = {
      "1001": "Cooperative", "1002": "Ajo", "1003": "Christmas Savings",
      "1004": "MEMBERS CLUB", "1005": "SCHOOL ACCOUNTING", "1006": "CBT APP",
      "1007": "HUMAN RESOURCE MANAGEMENT", "1009": "SALES APP",
      "1010": "HR CONSULTING BUSINESS", "1011": "GIFTING APP",
      "1014": "HOTEL MANAGEMENT SYSTEM", "1015": "GEA-ACCOUNTING APP"
    };
    return types[typeId] || "Unknown";
  };

  const getBusinessPricing = (typeId: string) => {
    const pricing: Record<string, { monthly: number; minMonths: number }> = {
      "1001": { monthly: 20000, minMonths: 12 },   // Cooperative Society
      "1002": { monthly: 10000, minMonths: 3 },    // Ajo
      "1003": { monthly: 10000, minMonths: 3 },    // Christmas Savings
      "1004": { monthly: 25000, minMonths: 2 },    // Club Management
      "1005": { monthly: 15000, minMonths: 6 },    // School Accounting
      "1006": { monthly: 15000, minMonths: 6 },    // CBT APP
      "1007": { monthly: 50000, minMonths: 6 },    // HR Management
      "1009": { monthly: 10000, minMonths: 3 },    // Sales/POS
      "1010": { monthly: 50000, minMonths: 6 },    // HR Consulting
      "1011": { monthly: 250000, minMonths: 3 },   // Gifting/Artisans
      "1014": { monthly: 15000, minMonths: 3 },    // Hotel Management
      "1015": { monthly: 15000, minMonths: 6 },    // Enterprise Accounting
    };
    return pricing[typeId] || { monthly: 10000, minMonths: 3 };
  };

  const getBusinessTypeColor = (typeId: string) => {
    const colors: Record<string, string> = {
      "1001": "#4caf50", "1002": "#2196f3", "1003": "#ff9800", "1004": "#f44336",
      "1005": "#3f51b5", "1006": "#9c27b0", "1007": "#009688", "1009": "#ff5722",
      "1010": "#8bc34a", "1011": "#8bc34a", "1014": "#8bc34a", "1015": "#8bc34a"
    };
    return colors[typeId] || "#9e9e9e";
  };

  const getBusinessTypeStaffButtonColor = (typeId: string) => {
    const colors: Record<string, string> = {
      "1010": "#388e3c", "1002": "#1976d2", "1003": "#f57c00", "1004": "#d32f2f",
      "1011": "#303f9f", "1006": "#7b1fa2", "1007": "#00796b", "1009": "#e64a19",
      "1014": "#a9330fff", "1001": "#689f38"
    };
    return colors[typeId] || "#757575";
  };

  const totalPages = Math.ceil(businesses.length / itemsPerPage);
  const paginatedBusinesses = businesses.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } };
  const itemVariants = { hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1, transition: { type: "spring", stiffness: 100, damping: 12 } } };

  return (
    <IonPage className="ion-padding" style={{ backgroundColor: "#f5f7fa" }}>
      <IonHeader className=" mdboard">
        <Box display="flex" justifyContent="space-between" p={2}>
          <Typography variant="h6" sx={{ color: "chocolate", flex: 1, display: 'flex', alignItems: 'center' }}>
            <Business className={styles.titleIcon} sx={{ mr: 1 }} />
            Merchant Dashboard
          </Typography>
          <Button variant="contained" color="primary" onClick={() => history.push("/folder/setUpBusiness")}>
            Add New Business
          </Button>
        </Box>
      </IonHeader>

      <IonContent className={styles.container}>
        {loading ? (
          <Grid container spacing={2}>
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <Grid item xs={12} sm={6} md={6} key={item}>
                <Card className={styles.skeletonCard}>
                  <CardContent>
                    <Skeleton variant="rectangular" height={30} width="60%" sx={{ mb: 2 }} />
                    <Skeleton variant="text" height={25} width="90%" />
                    <Skeleton variant="text" height={25} width="80%" />
                    <Skeleton variant="text" height={25} width="70%" />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <>
            <motion.div variants={containerVariants} initial="hidden" animate="visible">
              <Grid container spacing={3}>
                {paginatedBusinesses.length > 0 ? (
                  paginatedBusinesses.map((business, index) => {
                    const isSubscribed = subscribedIds.has(business.business_id);
                    return (
                      <Grid item xs={12} sm={6} md={6} key={business.id || index}>
                        <motion.div variants={itemVariants} className={styles.cardWrapper} whileHover={{ y: -5 }}>
                          <Card className={styles.businessCard}>
                            <Box className={styles.cardHeader} sx={{ backgroundColor: getBusinessTypeColor(business.business_type_id) }}>
                              <Typography variant="h6" className={styles.businessName}>{business.name}</Typography>
                              <Chip label={getBusinessType(business.business_type_id)} size="small" className={styles.typeChip} />
                            </Box>
                            <CardContent className={styles.cardContent}>
                              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                <Box>
                                  <Typography className={styles.businessInfo}><strong>Address:</strong> {business.address}</Typography>
                                  <Typography className={styles.businessInfo}><strong>Phone:</strong> {business.phone}</Typography>
                                  <Typography className={styles.businessInfo}><strong>Email:</strong> {business.email}</Typography>
                                </Box>
                                {isSubscribed && (
                                  <Chip 
                                    icon={<Verified sx={{ color: 'white !important' }} />} 
                                    label="ACTIVE SUBSCRIPTION" 
                                    color="success" 
                                    sx={{ fontWeight: 'bold', fontSize: '0.7rem' }} 
                                  />
                                )}
                              </Box>

                              <Box className={styles.actionButtons} sx={{ gap: 2 }}>
                                {!isSubscribed ? (
                                  <Button
                                    variant="contained"
                                    startIcon={<Payments />}
                                    size="small"
                                    onClick={() => openSubscriptionDialog(business)}
                                    sx={{ 
                                      fontWeight: 'bold',
                                      background: 'linear-gradient(45deg, #ff5722 30%, #ff9800 90%)',
                                      color: 'white',
                                      '&:hover': {
                                        background: 'linear-gradient(45deg, #e64a19 30%, #f57c00 90%)',
                                      }
                                    }}
                                  >
                                    Subscribe Now
                                  </Button>
                                ) : (
                                  <Button
                                    variant="contained"
                                    color="success"
                                    disabled
                                    size="small"
                                    sx={{ opacity: 1, bgcolor: '#4caf50 !important', color: 'white !important' }}
                                  >
                                    Active Subscription
                                  </Button>
                                )}

                                <Button
                                  variant="contained"
                                  color="secondary"
                                  startIcon={<People />}
                                  size="small"
                                  disabled={!isSubscribed}
                                  className={styles.staffButton}
                                  style={{
                                    backgroundColor: isSubscribed ? getBusinessTypeStaffButtonColor(business.business_type_id) : '#ccc',
                                  }}
                                  onClick={() => handleManageStaff(business)}
                                >
                                  View Business
                                </Button>
                              </Box>
                            </CardContent>
                          </Card>
                        </motion.div>
                      </Grid>
                    );
                  })
                ) : (
                  <Grid item xs={12}>
                    <Card className={styles.emptyCard}>
                      <CardContent>
                        <Typography variant="h6" align="center">No businesses found.</Typography>
                        <Typography align="center" color="textSecondary">Click "Add New Business" to create your first business.</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </motion.div>

            {businesses.length > itemsPerPage && (
              <Box className={styles.paginationContainer}>
                <Pagination count={totalPages} page={page} onChange={(_, value) => setPage(value)} color="primary" size={isMobile ? "small" : "medium"} />
              </Box>
            )}
          </>
        )}

        {/* Subscription Dialog */}
        <Dialog 
          open={subscriptionDialogOpen} 
          onClose={() => !paymentProcessing && activeStep !== 2 && setSubscriptionDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ textAlign: 'center', bgcolor: '#f8f9fa', borderBottom: '1px solid #eee', fontWeight: 'bold' }}>
            <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
              <Payments color="primary" />
              Business Subscription
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <Tabs 
              value={activeStep} 
              variant="fullWidth" 
              indicatorColor="primary" 
              textColor="primary"
              sx={{ borderBottom: '1px solid #eee' }}
            >
              <Tab label="1. Plan" disabled={activeStep !== 0} />
              <Tab label="2. Payment" disabled={activeStep !== 1} />
              <Tab label="3. Complete" disabled={activeStep !== 2} />
            </Tabs>

            <Box sx={{ p: 4 }}>
              <AnimatePresence mode="wait">
                {activeStep === 0 && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} key="step0">
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Select a subscription plan for {selectedBusinessForSub?.name}
                    </Typography>
                    {selectedBusinessForSub && (() => {
                      const { monthly, minMonths } = getBusinessPricing(selectedBusinessForSub.business_type_id);
                      const annual = monthly * 12 * 0.8; // 20% discount
                      return (
                        <RadioGroup defaultValue="monthly">
                          <Box sx={{ p: 2, border: '2px solid #ff5722', bgcolor: '#fff3e0', borderRadius: 2, mb: 2 }}>
                            <FormControlLabel value="monthly" control={<Radio sx={{ color: '#ff5722', '&.Mui-checked': { color: '#ff5722' } }} />} label={
                              <Box>
                                <Typography fontWeight="bold">Monthly Plan</Typography>
                                <Typography variant="h6" sx={{ color: '#e64a19' }}>
                                  {formatCurrency(monthly)} <Typography variant="caption" component="span" color="textSecondary">/ month</Typography>
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  Minimum commitment: {minMonths} months ({formatCurrency(monthly * minMonths)})
                                </Typography>
                              </Box>
                            } />
                          </Box>
                          <Box sx={{ p: 2, border: '1px solid #ddd', borderRadius: 2 }}>
                            <FormControlLabel value="yearly" control={<Radio sx={{ '&.Mui-checked': { color: '#ff5722' } }} />} label={
                              <Box>
                                <Typography fontWeight="bold">Annual Plan (Save 20%)</Typography>
                                <Typography variant="h6" sx={{ color: '#e64a19' }}>
                                  {formatCurrency(annual)} <Typography variant="caption" component="span" color="textSecondary">/ year</Typography>
                                </Typography>
                                <Typography variant="body2" color="textSecondary">Full access for 12 months</Typography>
                              </Box>
                            } />
                          </Box>
                        </RadioGroup>
                      );
                    })()}
                  </motion.div>
                )}

                {activeStep === 1 && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} key="step1">
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Secure Payment Information</Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <TextField fullWidth label="Cardholder Name" placeholder="John Doe" variant="outlined" />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth label="Card Number" placeholder="0000 0000 0000 0000" InputProps={{ startAdornment: <InputAdornment position="start"><CreditCard color="action" /></InputAdornment> }} />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField fullWidth label="Expiry Date" placeholder="MM/YY" />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField fullWidth label="CVV" placeholder="123" type="password" />
                      </Grid>
                    </Grid>
                    
                    <Box sx={{ mt: 3, p: 2, bgcolor: '#fff9c4', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Verified sx={{ color: '#fbc02d' }} />
                      <Typography variant="caption">Your transaction is encrypted and secured by AppStack Payments.</Typography>
                    </Box>

                    {paymentProcessing && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3 }}>
                        <CircularProgress size={40} sx={{ mb: 1 }} />
                        <Typography variant="body2" fontWeight="bold" color="primary">Processing Payment...</Typography>
                      </Box>
                    )}
                  </motion.div>
                )}

                {activeStep === 2 && (
                  <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} key="step2">
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <Box sx={{ 
                        width: 80, height: 80, bgcolor: '#e8f5e9', borderRadius: '50%', 
                        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', mb: 2
                      }}>
                        <CheckCircle sx={{ fontSize: 60, color: '#4caf50' }} />
                      </Box>
                      <Typography variant="h5" fontWeight="bold" gutterBottom>Subscription Activated!</Typography>
                      <Typography color="textSecondary" mb={3}>
                        Congratulations! Your business "{selectedBusinessForSub?.name}" is now fully activated. 
                        You can now manage staff and access all features.
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      <Typography variant="body2" color="textSecondary">
                        Transaction Reference: #GEA-{Math.floor(Math.random() * 1000000)}
                      </Typography>
                    </Box>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 4, pb: 4, pt: 0 }}>
            {activeStep === 0 && (
              <Button fullWidth variant="contained" size="large" onClick={handleNextStep} sx={{ py: 1.5 }}>
                Continue to Payment
              </Button>
            )}
            {activeStep === 1 && (
              <Button 
                fullWidth 
                variant="contained" 
                size="large" 
                onClick={handleNextStep} 
                disabled={paymentProcessing}
                sx={{ py: 1.5, bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' } }}
              >
                {paymentProcessing ? "Processing..." : "Complete Payment"}
              </Button>
            )}
            {activeStep === 2 && (
              <Button fullWidth variant="contained" color="success" size="large" onClick={handleNextStep} sx={{ py: 1.5 }}>
                Manage Business Now
              </Button>
            )}
          </DialogActions>
        </Dialog>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className={styles.summarySection}>
          <Typography variant="h5" component="h3" className={styles.summaryTitle}>Summary</Typography>
          <Card className={styles.summaryCard}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box className={styles.statBox}>
                    <Typography variant="subtitle2" color="springgreen">Total Businesses</Typography>
                    <Typography variant="h4">{businesses.length}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box className={styles.statBox}>
                    <Typography variant="subtitle2" color="burlywood">Subscribed Businesses</Typography>
                    <Typography variant="h4">{subscribedIds.size}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </motion.div>
      </IonContent>
    </IonPage>
  );
};

export default BusinessDashboard;
